<template>
  <form
    class="login-root"
    @submit.prevent
  >
    <h2
      v-if="showTitle"
      class="subtitle"
      style="margin-top: 0.5em; margin-bottom: 0.5em"
    >
      Login/Register
    </h2>

    <GoogleLogin />

    <TextInput
      v-model="form.username"
      label="Username"
    />
    <button
      type="submit"
      id="check-user"
      class="button is-info"
      @click="checkUser"
      v-if="!userChecked"
    >
      Next
    </button>

    <div v-if="userIsUnknown">
      <p class="content notification">
        {{ form.username }} is niet bekend, wil je een account aanmaken?<br />
        <slot name="disclaimer" />
      </p>
      <button
        type="button"
        id="register"
        class="button is-info"
        @click="webauthnRegister"
      >
        Account aanmaken
      </button>
    </div>

    <div v-if="userHasGoogle && !userHasWebauthn && !userHasPassword">
      <p class="content">Log in met Google door op de knop hierboven te drukken.</p>
    </div>

    <div v-if="userHasWebauthn">
      <p class="content">Beantwoord de popup...</p>
    </div>

    <div v-if="userHasPassword && !userHasWebauthn">
      <div class="field">
        <label
          for="password"
          class="label"
        >Password</label>
        <div class="control">
          <input
            v-model="form.password"
            class="input"
            type="password"
            name="password"
            id="password"
            required
          />
        </div>
      </div>
      <div class="buttons">
        <button
          type="submit"
          id="password-login"
          class="button is-info"
          @click="passwordLogin"
        >
          Login
        </button>
        <RouterLink
          :to="{ name: 'accounts:add-new-device' }"
          class="button"
        >Dit apparaat toevoegen</RouterLink>
      </div>
      <div
        class="notification is-danger"
        v-if="authStore.authError"
        style="margin-top: 1em"
      >
        Login failed
      </div>
    </div>

    <div v-if="!userHasPassword && webauthnFailed">
      <div class="notification">Inlog met webauthn mislukt</div>
      <p class="mb-4">Is dit een nieuw apparaat en wil je hem toevoegen?</p>
      <RouterLink
        :to="{ name: 'accounts:add-new-device' }"
        class="button"
      >Dit apparaat toevoegen</RouterLink>
    </div>
  </form>
</template>

<script
  lang="ts"
  setup
>
import { create, get, parseCreationOptionsFromJSON, parseRequestOptionsFromJSON } from "@github/webauthn-json/browser-ponyfill"
import { nextTick, ref, watch } from 'vue'
import TextInput from '../../components/inputs/TextInput.vue'
import notify from '../../notifications/notify'
import { loginState, postCheckUser, startWebAuthnLogin, webAuthnAuthenticate, webAuthnFinishRegistration, webAuthnStartRegistration } from "../api"
import { useAuthStore } from '../authStore'
import GoogleLogin from "./GoogleLogin.vue"

const authStore = useAuthStore()

const { showTitle = true } = defineProps<{
  showTitle?: boolean
}>()

const form = ref({
  username: "",
  password: ""
})

const userChecked = ref(false)
const userHasPassword = ref(false)
const userIsUnknown = ref(false)
const userHasGoogle = ref(false)
const userHasWebauthn = ref(false)
const webauthnFailed = ref(false)

watch(() => form.value.username, () => {
  userChecked.value = false
  userHasPassword.value = false
  userIsUnknown.value = false
  userHasWebauthn.value = false
  userHasGoogle.value = false
})

function passwordLogin() {
  authStore.passwordLogin(form.value)
}

async function checkUser() {
  if (form.value.username === "") {
    return
  }
  const response = await postCheckUser({ username: form.value.username })
  userIsUnknown.value = response.result.includes('unknown')
  userHasPassword.value = response.result.includes("password")
  userHasWebauthn.value = response.result.includes("webauthn")
  userHasGoogle.value = response.result.includes("google")
  userChecked.value = true

  if (userHasWebauthn.value) {
    nextTick(() => webauthnLogin())
  } else if (userHasPassword.value) {
    nextTick(() => {
      document.getElementById("password")?.focus()
    })
  }
}

async function webauthnLogin() {
  try {
    const response = await startWebAuthnLogin({ username: form.value.username })
    const authCred = await get(parseRequestOptionsFromJSON({ publicKey: response }))
    await webAuthnAuthenticate(authCred)
    whenAuthenticated()
  } catch {
    notify({
      style: 'error',
      text: 'Inloggen mislukt'
    })
    webauthnFailed.value = true
    userHasWebauthn.value = false
  }
}

async function webauthnRegister() {
  try {
    const response = await webAuthnStartRegistration({ username: form.value.username })
    const credential = await create(parseCreationOptionsFromJSON({ publicKey: response }))
    await webAuthnFinishRegistration(credential)
    whenAuthenticated()
  } catch {
    notify({
      style: 'error',
      text: 'Registratie mislukt'
    })
    form.value.username = ''
  }
}

async function whenAuthenticated() {
  const response = await loginState()
  if (response.authenticated) {
    authStore.loginSuccess(response.user)
  } else {
    authStore.setLogout()
  }
}
</script>
